import React, { FormEventHandler, Fragment, ReactNode } from "react";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogProps,
  IconButton,
  makeStyles,
  Theme,
  Tooltip,
  Typography
} from "@material-ui/core";

import ModalHead from "../components/ModalHead";
import ModalBody from "../components/ModalBody";
import ModalFooter from "../components/ModalFooter";

import { SubmitHandler } from "react-hook-form";
import { MdClose } from "react-icons/md";

type ModalType = "success" | "warning" | "error" | "primary";

const useStyles = makeStyles<Theme, { type: ModalType }>(theme => ({
  headRightContent: {
    display: "flex",
    alignItems: "center"
  },

  submitBtn: {
    backgroundColor: ({ type }) => theme.palette[type]?.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: ({ type }) => theme.palette[type]?.dark
    }
  }
}));

interface IProps extends Omit<DialogProps, "title"> {
  open: boolean;
  title: ReactNode;
  type: ModalType;
  submitLabel?: string;
  cancelLabel?: string;
  isForm?: boolean;
  onSubmit: FormEventHandler | SubmitHandler<any>;
  onClose: () => void;
  isSubmitDisabled?: boolean;
  isLoading?: boolean;
  children: React.ReactNode;
  actionBtns?: React.ReactNode;
  submitBtnIcon?: React.ReactNode;
  hideCancelBtn?: boolean;
  stepsCount?: number;
  currentStep?: number;
  withCloseIcon?: boolean;
  headRightSection?: ReactNode;
}

const Modal: React.FC<IProps> = props => {
  const {
    open,
    title,
    children,
    type,
    submitLabel = "Submit",
    cancelLabel = "Cancel",
    isForm = false,
    isSubmitDisabled = false,
    onSubmit,
    onClose,
    isLoading,
    actionBtns,
    submitBtnIcon,
    hideCancelBtn,
    stepsCount,
    currentStep,
    withCloseIcon,
    headRightSection,
    ...restProps
  } = props;

  const InnerComponent = isForm ? "form" : Fragment;

  const classes = useStyles({ type });

  const showStepsLabel =
    typeof stepsCount === "number" && typeof currentStep === "number";

  return (
    <Dialog open={open} onClose={onClose} scroll="body" {...restProps}>
      <InnerComponent
        {...(isForm
          ? {
              onSubmit,
              autoComplete: "off",
              autoCorrect: "off",
              noValidate: true
            }
          : {})}
      >
        <ModalHead
          title={title}
          rightContent={
            <div className={classes.headRightContent}>
              {showStepsLabel && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                >{`Step ${currentStep} of ${stepsCount}`}</Typography>
              )}

              {headRightSection}

              {withCloseIcon && (
                <Tooltip title="Close">
                  <IconButton
                    onClick={onClose}
                    style={{ marginLeft: showStepsLabel ? 15 : 0 }}
                  >
                    <MdClose />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          }
        />

        <ModalBody>{children}</ModalBody>

        <ModalFooter>
          {!hideCancelBtn && (
            <Button
              onClick={onClose}
              variant="text"
              size="small"
              disabled={isLoading}
            >
              {cancelLabel}
            </Button>
          )}

          {actionBtns}

          <Button
            type={isForm ? "submit" : "button"}
            className={classes.submitBtn}
            color="inherit"
            size="small"
            endIcon={
              isLoading ? (
                <CircularProgress size={14} color="primary" />
              ) : (
                submitBtnIcon
              )
            }
            disabled={isSubmitDisabled || isLoading}
            variant="contained"
            {...(isForm ? {} : { onClick: onSubmit })}
          >
            {submitLabel}
          </Button>
        </ModalFooter>
      </InnerComponent>
    </Dialog>
  );
};

export default Modal;
